import * as linkify from 'linkifyjs';
import linkifyHtml from 'linkify-html';
import DOMPurify from 'dompurify';
// Server-only. config/webpack/clientWebpackConfig.js aliases this to `false`
// (an empty module), so it never ships in the browser bundles (#176).
import serverSanitizeHtml from 'sanitize-html';

export function escapeHtml(unsafe) {
  return unsafe
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

export function simpleFormat(_str) {
  if (!_str) return;
  let str = _str.replace(/\r\n?/, '\n');
  str = str.trim();
  str = escapeHtml(str);
  str = linkifyHtml(str, {});
  if (str.length > 0) {
    str = str.replace(/\n\n+/g, '</p><p>');
    str = str.replace(/\n/g, '<br />');
    str = `<p>${str}</p>`;
  }
  return str;
}

// One allowlist for both sanitizers.
const ALLOWED_TAGS = ['p', 'br', 'b', 'i', 'em', 'strong', 'a', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'img', 'span', 'div'];
const ALLOWED_ATTR = ['href', 'src', 'alt', 'target', 'rel', 'class'];

// sanitize-html options that reproduce the DOMPurify behaviour above, so the
// server and the client produce the same DOM for normal content and hydration
// does not mismatch:
// - URL schemes: DOMPurify's default ALLOWED_URI_REGEXP list, plus data: on
//   <img src> (DOMPurify's DATA_URI_TAGS). Relative and protocol-relative URLs
//   are allowed by both. Entity-encoded schemes (jav&#x61;script:) are decoded
//   before the check.
// - Disallowed tags are dropped but keep their text, except raw-text elements
//   (script, style, iframe, ...) whose content DOMPurify drops too. Not svg or
//   math: an unclosed <svg> would swallow the rest of the body on the server,
//   while the browser breaks out of it at the next HTML tag.
const SANITIZE_HTML_OPTIONS = {
  allowedTags: ALLOWED_TAGS,
  allowedAttributes: { '*': ALLOWED_ATTR },
  allowedSchemes: ['http', 'https', 'ftp', 'ftps', 'mailto', 'tel', 'callto', 'sms', 'cid', 'xmpp', 'matrix'],
  allowedSchemesByTag: {
    img: ['http', 'https', 'ftp', 'ftps', 'mailto', 'tel', 'callto', 'sms', 'cid', 'xmpp', 'matrix', 'data'],
  },
  allowedSchemesAppliedToAttributes: ['href', 'src'],
  allowProtocolRelative: true,
  disallowedTagsMode: 'discard',
  nonTextTags: ['script', 'style', 'textarea', 'option', 'noscript', 'title', 'xmp', 'iframe', 'noembed', 'noframes'],
};

/*
 * sanitizeHtml — allowlist-sanitize an HTML string for dangerouslySetInnerHTML.
 *
 * Browser: DOMPurify. Server (the SSR Node bundle, where DOMPurify loads as a
 * factory with no `sanitize` because there is no window): sanitize-html with
 * the same allowlist, so server-rendered pages keep their headings, links and
 * images. This path used to return the input unmodified (#176).
 *
 * Fails safe: if neither sanitizer is usable, or the server one throws, the
 * input is HTML-escaped (shown as text), never passed through.
 */
export function sanitizeHtml(html) {
  if (!html) return '';
  if (typeof DOMPurify.sanitize === 'function' && DOMPurify.isSupported) {
    return DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR });
  }
  if (typeof serverSanitizeHtml === 'function') {
    try {
      return serverSanitizeHtml(String(html), SANITIZE_HTML_OPTIONS);
    } catch (e) {
      // fall through to escaping
    }
  }
  return escapeHtml(String(html));
}
